import { getApplicationSettings } from 'owa-application-settings';
import { logUsage } from 'owa-analytics';
import { tryGetGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import {
    classifyApplinkAccountMatch,
    type ApplinkAccountClassification,
} from 'owa-mailbox-info/lib/classifyApplinkAccountMatch';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import getRootVdirName from 'owa-url/lib/getRootVdirName';
import getNewScope from 'owa-url/lib/getNewScope';
import { lazySanitizeUserProvidedPathSegment } from 'owa-url/lib/lazySanitizeUrlPathSegment';

let assignLocation = (url: string) => window.location.assign(url);

export interface ApplinkAccountRedirectContext {
    routeKey: string | undefined;
    url: URL;
}

export function getApplinkAccountRedirectContext(
    routeKey: string | undefined
): ApplinkAccountRedirectContext {
    return {
        routeKey,
        url: new URL(window.location.href),
    };
}

export function setLocationAssignForUnitTests(assigner?: (url: string) => void): void {
    assignLocation = assigner ?? ((url: string) => window.location.assign(url));
}

/**
 * For OWA applink routes, checks whether the route key targets a shared mailbox and redirects
 * through explicit logon if necessary. Returns `true` if a redirect was initiated (caller should
 * abort), or `false` if no redirect is needed (caller should proceed normally).
 *
 * This function is a no-op in Monarch — it only executes explicit-logon redirect logic for OWA.
 *
 * @param context The route key and source URL captured when the applink route was matched
 * @returns `true` if a redirect was initiated; `false` otherwise
 */
export function tryRedirectForSharedOrUnresolvedAccount(
    context: ApplinkAccountRedirectContext
): Promise<boolean> {
    const { routeKey, url } = context;

    // No-op for Monarch or when there's no route key
    if (!routeKey || getApplicationSettings('PlatformType').isMonarch) {
        return Promise.resolve(false);
    }

    if (
        !isGlobalFeatureEnabled('acct-resolvemailboxfromroutekey') ||
        !isGlobalFeatureEnabled('acct-explicitlogonredirectfromroutekey')
    ) {
        return Promise.resolve(false);
    }

    const classification = classifyApplinkAccountMatch(routeKey, url.searchParams);

    if (classification.matchType !== 'shared-mailbox') {
        return Promise.resolve(false);
    }

    logRedirectAttempt(classification);
    return redirectWithExplicitLogon(classification.destinationSmtp, url);
}

/**
 * Redirects using OWA explicit logon by inserting the destination SMTP as a URL scope segment.
 *
 * Example: `/mail/MBX:.../applink/compose/?ds=shared@ms.com`
 * becomes: `/mail/shared@ms.com/MBX:.../applink/compose/?ds=shared@ms.com`
 */
async function redirectWithExplicitLogon(
    destinationSmtp: string | undefined,
    sourceUrl: URL
): Promise<boolean> {
    if (!destinationSmtp) {
        return false;
    }

    const rootVdir = getRootVdirName() || 'mail';
    const currentUrl = new URL(sourceUrl.toString());
    const vdirPrefix = `/${rootVdir}/`;
    const pathAfterVdir = (
        currentUrl.pathname.startsWith(vdirPrefix)
            ? currentUrl.pathname.substring(vdirPrefix.length)
            : currentUrl.pathname
    ).replace(/^\/+/, '');

    // Sanitize each SMTP segment as a user-provided URL path segment (CSPT protection).
    const sanitize = await lazySanitizeUserProvidedPathSegment.import();
    const encodedTargetMailbox = destinationSmtp
        .split('@')
        .map(segment => sanitize(segment))
        .join('@');

    if (pathAfterVdir.split('/')[0]?.toLowerCase() === encodedTargetMailbox.toLowerCase()) {
        return false;
    }

    currentUrl.pathname = `${getNewScope(encodedTargetMailbox)}${pathAfterVdir}`;

    assignLocation(currentUrl.toString());
    return true;
}

function logRedirectAttempt(classification: ApplinkAccountClassification): void {
    logUsage('ApplinkAccountRedirect', tryGetGlobalSettingsAccountMailboxInfo(), {
        matchType: classification.matchType,
        hasDestinationSmtp: !!classification.destinationSmtp,
    });
}
